import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  FlatList,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { StepHeader, RadioButton, maintenanceStepStyles } from './shared';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface StepCategorySelectionProps {
  userName: string;
  requestType: 'maintenance' | 'it';
  onRequestTypeChange: (type: 'maintenance' | 'it') => void;
  subCategory: string;
  onSubCategoryChange: (value: string) => void;
  subCategoryOptions: string[];
}

/**
 * StepCategorySelection - Step 1: Category & Subcategory selection
 * Allows user to select between Maintenance and IT categories
 */
export const StepCategorySelection: React.FC<StepCategorySelectionProps> = ({
  userName,
  requestType,
  onRequestTypeChange,
  subCategory,
  onSubCategoryChange,
  subCategoryOptions,
}) => {
  const [showSubcategoryPicker, setShowSubcategoryPicker] = useState(false);
  const isMaintenanceSelected = requestType === 'maintenance';
  const isITSelected = requestType === 'it';

  // Check if we should show subcategory - always show when maintenance is selected
  const showSubcategory = isMaintenanceSelected;

  const handleMaintenanceSelect = () => {
    onRequestTypeChange('maintenance');
  };

  const handleITSelect = () => {
    onRequestTypeChange('it');
    onSubCategoryChange(''); // Clear subcategory when switching to IT
  };

  const handleSubcategorySelect = (value: string) => {
    onSubCategoryChange(value);
    setShowSubcategoryPicker(false);
  };

  return (
    <View style={maintenanceStepStyles.container}>
      <StepHeader 
        title={`Hi ${userName},`}
        subtitle="what do you need help with?"
      />
      
      <View style={maintenanceStepStyles.content}>
        {/* Maintenance Category */}
        <TouchableOpacity
          style={styles.categoryCard}
          onPress={handleMaintenanceSelect}
          activeOpacity={0.7}
        >
          <View style={styles.categoryIconContainer}>
            <RESIDENTS_SVG_COMPONENTS.MAINTENANCE 
              width={20} 
              height={20} 
              color={MARKETPLACE_COLORS.textPrimary} 
            />
          </View>
          
          <View style={styles.categoryContentContainer}>
            <View style={styles.categoryHeaderRow}>
              <View style={styles.categoryInfo}>
                <Text style={styles.categoryTitle}>Maintenance</Text>
                <Text style={styles.categoryDescription}>
                  Report issues related to repairs or upkeep
                </Text>
              </View>
              <RadioButton selected={isMaintenanceSelected} />
            </View>
            
            {/* Subcategory Dropdown - Show inside card when maintenance is selected */}
            {showSubcategory && (
              <TouchableOpacity
                style={styles.selectContainer}
                onPress={() => setShowSubcategoryPicker(true)}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.selectText,
                  subCategory && styles.selectTextFilled,
                ]}>
                  {subCategory || 'Subcategory'}
                </Text>
                <DropdownIcon />
              </TouchableOpacity>
            )}
          </View>
        </TouchableOpacity>

        {/* IT Category */}
        <TouchableOpacity
          style={styles.categoryCard}
          onPress={handleITSelect}
          activeOpacity={0.7}
        >
          <View style={styles.categoryIconContainer}>
            <RESIDENTS_SVG_COMPONENTS.NO_CONNECTION 
              width={20} 
              height={20} 
              color={MARKETPLACE_COLORS.textPrimary} 
            />
          </View>
          
          <View style={styles.categoryContentContainer}>
            <View style={styles.categoryHeaderRow}>
              <View style={styles.categoryInfo}>
                <Text style={styles.categoryTitle}>IT</Text>
                <Text style={styles.categoryDescription}>
                  Report issues with the app or system
                </Text>
              </View>
              <RadioButton selected={isITSelected} />
            </View>
          </View>
        </TouchableOpacity>
      </View>

      {/* Subcategory Picker Modal */}
      <Modal
        visible={showSubcategoryPicker}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowSubcategoryPicker(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1}
          onPress={() => setShowSubcategoryPicker(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Subcategory</Text>
              <TouchableOpacity onPress={() => setShowSubcategoryPicker(false)}>
                <Text style={styles.modalClose}>Cancel</Text>
              </TouchableOpacity>
            </View>
            {subCategoryOptions.length > 0 ? (
              <FlatList
                data={subCategoryOptions}
                keyExtractor={(item, index) => `${item}-${index}`}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.modalOption}
                    onPress={() => handleSubcategorySelect(item)}
                  >
                    <Text style={[
                      styles.modalOptionText,
                      item === subCategory && styles.modalOptionTextSelected,
                    ]}>
                      {item}
                    </Text>
                    {item === subCategory && (
                      <View style={styles.checkmark} />
                    )}
                  </TouchableOpacity>
                )}
              />
            ) : (
              <View style={styles.modalEmptyState}>
                <Text style={styles.modalEmptyText}>No subcategories available</Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// Dropdown Icon Component
const DropdownIcon: React.FC = () => (
  <View style={styles.dropdownIcon}>
    <View style={styles.dropdownArrow} />
  </View>
);

const styles = StyleSheet.create({
  categoryCard: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    flexDirection: 'row',
    gap: 16,
  },
  categoryIconContainer: {
    width: 46,
    height: 46,
    borderRadius: 50,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryContentContainer: {
    flex: 1,
    gap: 16,
  },
  categoryHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  categoryInfo: {
    flex: 1,
    gap: 6,
  },
  categoryTitle: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  categoryDescription: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  
  // Select/Dropdown
  selectContainer: {
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.textPrimary,
  },
  selectText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 24,
  },
  selectTextFilled: {
    color: MARKETPLACE_COLORS.textPrimary,
  },
  
  // Dropdown Icon
  dropdownIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropdownArrow: {
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderTopWidth: 5,
    borderStyle: 'solid',
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: MARKETPLACE_COLORS.textMuted,
  },
  
  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
  },
  modalClose: {
    fontSize: 16,
    color: MARKETPLACE_COLORS.accentBlue,
    fontWeight: '500',
  },
  modalOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalOptionText: {
    fontSize: 16,
    color: MARKETPLACE_COLORS.textPrimary,
  },
  modalOptionTextSelected: {
    color: MARKETPLACE_COLORS.accentRed,
    fontWeight: '500',
  },
  modalEmptyState: {
    padding: 32,
    alignItems: 'center',
  },
  modalEmptyText: {
    fontSize: 14,
    color: MARKETPLACE_COLORS.textSecondary,
  },
  checkmark: {
    width: 8,
    height: 14,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    borderColor: MARKETPLACE_COLORS.accentRed,
    transform: [{ rotate: '45deg' }],
  },
});

export default StepCategorySelection;
